/**
 * Traffic (`/traffic`) — the active site's real-user audience view.
 *
 * Renamed from "Analytics" 2026-05-30 as part of the Performance /
 * Traffic / Behaviours / Sales IA split. Performance-side surfaces
 * (CWV, audits) now live under `/performance/*`; this route keeps the
 * visitors / sessions / sources / geo / devices lens.
 *
 * Renders the openpanel-shaped surfaces from
 * `components/rum/RumPlaceholders.tsx` and threads pixel data through
 * them. Live data comes from a single bundle endpoint call
 * (`/v1/sites/{uuid}/pixel/bundle`) which fans out to 8 Tinybird pipes
 * in parallel server-side so the cold load is ~250ms p75 instead of
 * the ~640ms it'd be with 8 separate RTTs. See
 * `plans/1.0.0/pixel-ingest/08-data-loading.md` §"API-side bundling".
 *
 * Each placeholder component already renders a polished
 * "no-data-yet" shape; data hooks fill the same DOM in-place when
 * beacons start flowing. The install banner stays visible only while
 * the pixel has no beacons today.
 */
import {
  DEFAULT_PAGE_RANGE,
  type PageRange,
  PageRangePill,
} from '@/components/primitives/PageRangePill';
import {
  RumBreakdownGrid,
  RumKpiRow,
  RumPlaceholderBanner,
  RumTrendChart,
} from '@/components/rum/RumPlaceholders';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import { useSitePixelBundle } from '@/lib/queries/pixel';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/traffic/')({
  component: AnalyticsPage,
});

function AnalyticsPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const [range, setRange] = useState<PageRange>(DEFAULT_PAGE_RANGE);

  // The bundle now covers the KPI row + KPI sparkline data + the
  // install-state gate (health). Trend and every breakdown card
  // self-fetch (keyed by siteUuid + their own params) so they re-
  // fetch correctly on a site switch.
  //
  // `range` is threaded through so widening / narrowing the window
  // re-fetches every surface coherently.
  const bundle = useSitePixelBundle(siteUuid, {
    days: range,
    surfaces: ['kpis', 'kpis_trend', 'health'],
  });

  const beaconsToday = bundle.data?.health?.beacons_today ?? 0;
  const kpis = bundle.data?.kpis;
  const hasAnyData = (kpis?.pageviews ?? 0) > 0 || (kpis?.sessions ?? 0) > 0;
  const installed = beaconsToday > 0 || hasAnyData;

  // Three states, distinguished only by the notice — the UI shapes
  // below render in every state so the layout never jumps:
  //   - loading : bundle still resolving → SHELL (shapes, no notice)
  //   - empty   : resolved + no data     → PRE-DATA (shapes + notice)
  //   - data    : has data               → real values, faded in
  // The notice is gated on `confirmedEmpty` (resolved AND empty), never
  // on the bare `!installed`, so the "install the pixel" CTA can't
  // flicker in during the loading shell of an already-installed site.
  // `awaitingFirstBeacon` only flips to the "installed but silent" copy
  // when `last_beacon_at` proves we've actually seen a beacon at some
  // point. The healthToday stub returns `1970-01-01 …` when there's no
  // row — using `health.data !== undefined` would always be true and
  // claim "snippet is installed" on sites with no pixel.
  const confirmedEmpty = bundle.isSuccess && !installed;
  const lastBeaconAt = bundle.data?.health?.last_beacon_at;
  const everSeenBeacon = !!lastBeaconAt && !lastBeaconAt.startsWith('1970');
  const awaitingFirstBeacon = confirmedEmpty && everSeenBeacon;

  return (
    <SiteIsland section="Traffic">
      {siteUuid && (
        <div className="space-y-6">
          {/* Stacks on a phone so the recap gets a full line instead of
              truncating mid-number next to the range pill; side-by-side from
              `sm` up where both fit. */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {/* Headline recap fills the otherwise-empty space left of the
                range pill. Always present (dashes while loading, then the
                real numbers — 0 on a site with no data yet). */}
            <div className="min-w-0 flex-1">
              <TrafficSummary kpis={kpis ?? undefined} loading={bundle.isLoading} />
            </div>
            <PageRangePill value={range} onChange={setRange} />
          </div>
          <RumPlaceholderBanner
            scope="site"
            siteUuid={siteUuid}
            hidden={!confirmedEmpty}
            awaitingFirstBeacon={awaitingFirstBeacon}
          />
          <RumKpiRow
            scope="site"
            data={bundle.data?.kpis ?? undefined}
            trend={bundle.data?.kpis_trend ?? undefined}
            range={range}
          />
          <RumTrendChart scope="site" siteUuid={siteUuid} range={range} />
          <RumBreakdownGrid
            cards={['pages', 'sources', 'geo', 'devices']}
            siteUuid={siteUuid}
            days={range}
            linkPages
          />
        </div>
      )}
    </SiteIsland>
  );
}

const compactFmt = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

/** One-line audience recap for the selected window — fills the space
 *  left of the range pill. Uses the KPI data already in the bundle.
 *  Shows em-dash placeholders while the bundle loads, then the real
 *  numbers (0 on a site with no data yet). */
function TrafficSummary({
  kpis,
  loading,
}: {
  kpis: { visitors: number; sessions: number } | undefined;
  loading: boolean;
}): ReactElement {
  const visitors = loading ? '—' : compactFmt.format(kpis?.visitors ?? 0);
  const sessions = loading ? '—' : compactFmt.format(kpis?.sessions ?? 0);
  return (
    <p className="truncate text-sm text-[var(--color-mute)]">
      <span className="font-semibold tabular-nums text-[var(--color-ink)]">{visitors}</span>{' '}
      visitors
      <span className="mx-1.5 text-[var(--color-line)]">·</span>
      <span className="font-semibold tabular-nums text-[var(--color-ink)]">{sessions}</span>{' '}
      sessions
    </p>
  );
}
